/**
 * Auto Sentinel — single Settings-embedded AI workbench (real APIs).
 * Soft UI only: no flag dumps, raw IDs, or backend jargon.
 * Normative: docs/plans/2026-07-26-incident-os-ai-srf-unified-master.md §2
 */

import { escapeHtml } from "./dom.js";
import { softDeviceLabel, softOperatorNote, softDurationSeconds } from "./format.js";
import { responseAiInnovHtml } from "./response-loop-workbench.js";
import { capabilityRegistrySoftHtml } from "./capability-registry-facade.js";

function softIntelStatus(raw) {
  const s = String(raw || "").trim().toLowerCase();
  if (!s || s === "—") return "—";
  if (s === "pending" || s === "open") return "Needs you";
  if (s === "approved" || s === "continuing") return "Continuing";
  if (s === "rejected") return "Held";
  if (s === "done" || s === "closed") return "Done";
  return softOperatorNote(s);
}

function softSelectedLabel(raw) {
  const s = String(raw || "").trim();
  if (!s) return "Auto plan";
  const bare = s.replace(/^ai[_-]?inc(?:ident)?[_-]?/i, "");
  const tail = String(bare || s).replace(/[^a-zA-Z0-9]/g, "").slice(-4).toUpperCase();
  return tail ? `Plan · ${tail}` : "Auto plan";
}

/** Soft priority band — never dump raw risk integers as the primary label. */
function softPriorityBand(score) {
  const n = Number(score);
  if (!Number.isFinite(n)) return "Priority · review";
  if (n >= 85) return "Priority · critical";
  if (n >= 70) return "Priority · high";
  if (n >= 40) return "Priority · medium";
  return "Priority · low";
}

function softConfidenceLabel(raw) {
  const s = String(raw || "").trim();
  if (!s || s === "—") return "";
  const n = Number(String(s).replace(/%/g, ""));
  if (!Number.isFinite(n)) return "";
  if (n >= 80) return "High certainty";
  if (n >= 50) return "Medium certainty";
  return "Low certainty";
}

function softLatencyLabel(raw) {
  const s = String(raw || "").trim();
  if (!s || s === "—") return "";
  return `Checked in ${s}`;
}

/** @param {object} it @param {string} id */
export function softAutoPlanLabel(it, id) {
  const bad = /^(unknown|n\/?a|null|undefined|—|-)$/i;
  const summary = softOperatorNote(String(it.summary || it.title || it.recommendation || it.detail || "")).trim();
  if (summary && !bad.test(summary) && summary.length >= 4) {
    return summary.length > 72 ? `${summary.slice(0, 70)}…` : summary;
  }
  const st = softIntelStatus(it.status);
  if (st === "Needs you") return "Plan waiting — interrupt?";
  if (st === "Continuing") return "Team continues";
  if (st === "Held") return "Held by you";
  if (st === "Done") return "Closed";
  const rawDev = String(it.device_id || "").trim();
  if (rawDev && !bad.test(rawDev)) {
    const device = softDeviceLabel(rawDev);
    if (device && device !== "—" && !bad.test(device) && !/^Unit ·/i.test(device)) return device;
  }
  return softSelectedLabel(id);
}

/**
 * @param {object} detail
 * @param {string} selectedId
 * @returns {string}
 */
export function buildAutoPlanDetailHtml(detail, selectedId) {
  const d = detail || {};
  const score = d.risk_score != null ? d.risk_score : (d.risk != null ? d.risk : "—");
  const confidence = d.confidence != null ? `${Math.round(Number(d.confidence) * 100)}%` : "—";
  const reasons = Array.isArray(d.reason) ? d.reason : (Array.isArray(d.reasons) ? d.reasons : []);
  const reasonText = reasons.length
    ? reasons.map((r) => escapeHtml(softOperatorNote(String(r)))).join("; ")
    : escapeHtml(softOperatorNote(String(d.summary || d.detail || "Rules set the priority. Written reasons appear when the model path is open.")));
  const recommendation = softOperatorNote(String(
    d.recommendation ||
    d.suggested_action ||
    (Array.isArray(d.actions) && d.actions.length ? d.actions.join(", ") : "") ||
    "Sentinel will continue unless you hold it.",
  ));
  const latencyRaw = d.latency_ms != null ? d.latency_ms : d.latency;
  const latency = latencyRaw != null && Number.isFinite(Number(latencyRaw))
    ? softDurationSeconds(Number(latencyRaw) / 1000) || `${Math.round(Number(latencyRaw))}s`
    : "—";
  const eventId = d.event_id != null ? String(d.event_id) : "";
  const category = softOperatorNote(String(
    d.category || d.classification || d.alarm_class || d.event_class || "Security review",
  ));
  const riskNum = Number(score);
  const escalateHint = !Number.isFinite(riskNum)
    ? "Open Home to Continue Team or Hold — this page is policy & history only."
    : riskNum >= 70
      ? "High risk — Needs You on Home before field action."
      : "On the loop — Continue Team; Needs You if it cannot close.";
  const escalateTitle = !Number.isFinite(riskNum)
    ? "Decide on Home"
    : riskNum >= 70
      ? "Needs You"
      : "On the loop";
  const peopleHint = eventId
    ? "After Continue Team on Home, assign nearby people from Assignments or Response team."
    : "Assign people from Assignments when you are ready for field follow-up.";
  const handoff = [];
  if (eventId) {
    handoff.push(`<button type="button" class="btn sm secondary btn-tap" data-cc-go="incidents" data-cc-id="${escapeHtml(eventId)}">Open incident</button>`);
    handoff.push(`<button type="button" class="btn sm secondary btn-tap" data-cc-go="orchestration?event_id=${encodeURIComponent(eventId)}">Response team</button>`);
  }
  handoff.push(`<button type="button" class="btn sm ghost btn-tap" data-cc-go="response">Assignments</button>`);
  return `<div class="croc-as-detail glass-panel glass-panel--inset" data-as-detail="1">
    <div class="croc-as-detail__head">
      <div>
        <p class="incident-workspace__eyebrow" style="margin:0 0 4px">This plan</p>
        <strong>${escapeHtml(softSelectedLabel(selectedId))}</strong>
      </div>
      <div class="croc-as-detail__acts">
        <button type="button" class="btn sm btn-tap" data-cc-go="overview">Home workbench</button>
        <button type="button" class="btn sm secondary btn-tap" data-cc-go="incidents">Open cases</button>
      </div>
    </div>
    <div class="croc-as-maic" role="list">
      <article class="croc-as-maic__card" role="listitem">
        <p class="croc-as-maic__k">Advisory analysis</p>
        <strong class="croc-as-maic__v">${escapeHtml(category)}</strong>
        <p class="muted croc-as-maic__s">${escapeHtml([softPriorityBand(score), softConfidenceLabel(confidence), softLatencyLabel(latency)].filter(Boolean).join(" · "))}</p>
        <p class="croc-as-maic__body">${reasonText}</p>
      </article>
      <article class="croc-as-maic__card" role="listitem">
        <p class="croc-as-maic__k">People</p>
        <strong class="croc-as-maic__v">Find who can go</strong>
        <p class="muted croc-as-maic__s">Prefer nearby · on duty · able to accept</p>
        <p class="croc-as-maic__body">${escapeHtml(peopleHint)}</p>
      </article>
      <article class="croc-as-maic__card" role="listitem">
        <p class="croc-as-maic__k">Escalation</p>
        <strong class="croc-as-maic__v">${escapeHtml(escalateTitle)}</strong>
        <p class="muted croc-as-maic__s">Notify stays first — planning never blocks the first call</p>
        <p class="croc-as-maic__body">${escapeHtml(escalateHint)}</p>
      </article>
    </div>
    <p style="margin-top:14px"><strong>Suggested next step</strong></p>
    <p style="line-height:1.45">${escapeHtml(recommendation)}</p>
    <div class="row" style="gap:8px;margin-top:12px;flex-wrap:wrap">${handoff.join("")}</div>
  </div>`;
}

/**
 * Unified Settings desk — pulse + policy + interrupt queue + scan (one surface).
 * @param {{
 *   aiOn?: boolean,
 *   items?: object[],
 *   settings?: object,
 *   selectedId?: string,
 *   detailHtml?: string,
 *   recentOptsHtml?: string,
 *   pulse?: { state?: string, line?: string },
 * }} [opts]
 * @returns {string}
 */
export function buildAutoSentinelSettingsWorkbenchHtml(opts = {}) {
  const aiOn = !!opts.aiOn;
  const items = Array.isArray(opts.items) ? opts.items : [];
  const settings = opts.settings || {};
  const selectedId = String(opts.selectedId || "").trim();
  const detailHtml = String(opts.detailHtml || "");
  const recentOptsHtml = String(opts.recentOptsHtml || `<option value="">No recent alarms yet</option>`);
  const tipsHtml = String(opts.tipsHtml || "");
  const mode = String(settings.mode || "rules");
  const thr = settings.auto_call_risk_threshold != null ? Number(settings.auto_call_risk_threshold) : 70;
  const humanGate = !!settings.require_human_approval;
  const waiting = items.filter((it) => {
    const st = String(it.status || "").toLowerCase();
    return !st || st === "pending" || st === "open";
  });
  const pulseState = String((opts.pulse && opts.pulse.state) || (waiting.length ? "interrupt" : "watching"));
  const pulseLine = String(
    (opts.pulse && opts.pulse.line) ||
      (waiting.length
        ? (waiting.length === 1 ? "1 plan needs your interrupt" : `${waiting.length} plans need your interrupt`)
        : humanGate
          ? "Constitution interrupt is on — not a user Hold-every-plan"
          : "The Watch is on the loop — Continue or Needs You"),
  );

  if (!aiOn) {
    return `
    <div class="croc-as-workbench" data-as-workbench="1">
      <header class="croc-as-workbench__hero glass-panel glass-panel--panel">
        <h2 class="croc-as-workbench__title">AI Policy</h2>
        <p class="muted croc-as-workbench__lede">Tonight's next step stays on Home. This page never Holds or Takes over. Scene text is preview.</p>
        <div class="glass-panel glass-panel--inset ops-inset" style="margin-top:14px">
          <span class="badge neutral">No plans waiting</span>
          <p class="ops-inset__hint">No plans waiting. The Watch plans new alarms automatically. Response plans, drills, and chat alerts stay usable now.</p>
          <div class="row" style="gap:8px;flex-wrap:wrap;margin-top:12px">
            <button type="button" class="btn sm btn-tap" data-cc-go="response-plans">Open plans</button>
            <button type="button" class="btn sm secondary btn-tap" data-cc-go="response-drill">Practice drill</button>
            <button type="button" class="btn sm secondary btn-tap" data-cc-go="overview">Home workbench</button>
          </div>
        </div>
        ${responseAiInnovHtml("ai")}
      </header>
    </div>`;
  }

  const stageWatch = pulseState === "watching" ? " is-lit" : "";
  const stageAct = pulseState === "acting" ? " is-lit" : "";
  const stageYou = pulseState === "interrupt" ? " is-lit" : "";

  const cards = (waiting.length ? waiting : items.slice(0, 8)).map((it) => {
    const id = String(it.incident_id || it.id || it.item_id || "");
    const label = softAutoPlanLabel(it, id);
    const st = String(it.status || "").toLowerCase();
    const canAct = !st || st === "pending" || st === "open";
    const risk = it.risk_score != null ? it.risk_score : (it.risk != null ? it.risk : null);
    const band = risk != null && Number.isFinite(Number(risk))
      ? (Number(risk) >= 85 ? "Priority · critical"
        : Number(risk) >= 70 ? "Priority · high"
          : Number(risk) >= 40 ? "Priority · medium" : "Priority · low")
      : "Priority · review";
    const on = selectedId && selectedId === id ? " is-selected" : "";
    const acts = canAct
      ? `<div class="croc-as-card__acts">
          <button type="button" class="btn sm btn-tap" data-cc-go="overview">Home workbench</button>
          <button class="btn sm ghost btn-tap" type="button" data-as-open="${escapeHtml(id)}">Why</button>
        </div>`
      : `<button class="btn sm ghost btn-tap" type="button" data-as-open="${escapeHtml(id)}">Details</button>`;
    return `
    <article class="croc-as-card glass-panel glass-panel--inset${on}" data-as-card="${escapeHtml(id)}">
      <div class="croc-as-card__top">
        <strong class="croc-as-card__title">${escapeHtml(label)}</strong>
        <span class="croc-as-card__risk">${escapeHtml(band)}</span>
      </div>
      <div class="croc-as-card__meta">
        <span class="badge ${canAct ? "warn" : "neutral"}">${escapeHtml(softIntelStatus(it.status))}</span>
        <span class="muted">${escapeHtml(softDeviceLabel(it.device_id))}</span>
      </div>
      ${acts}
    </article>`;
  }).join("");

  const queueBody = cards
    || `<p class="muted croc-as-empty">No plans waiting. The Watch plans new alarms automatically.</p>`;

  return `
  <div class="croc-as-workbench" data-as-workbench="1">
    <header class="croc-as-workbench__hero glass-panel glass-panel--panel">
      <div class="croc-as-workbench__hero-row">
        <div>
          <h2 class="croc-as-workbench__title">AI Policy</h2>
          <p class="muted croc-as-workbench__lede">Tonight's next step stays on Home. This page never Holds or Takes over. Scene text is preview.</p>
        </div>
        <span class="badge ${waiting.length ? "warn" : "online"} intelligence-auto-pill">${waiting.length ? "Needs you" : "No plans waiting"}</span>
      </div>
      <div class="croc-as-pulse" data-as-pulse="${escapeHtml(pulseState)}" aria-live="polite">
        <div class="croc-as-pulse__stages" role="list">
          <span class="croc-as-pulse__stage${stageWatch}" role="listitem"><i aria-hidden="true"></i>Watching</span>
          <span class="croc-as-pulse__stage${stageAct}" role="listitem"><i aria-hidden="true"></i>Acting</span>
          <span class="croc-as-pulse__stage${stageYou}" role="listitem"><i aria-hidden="true"></i>Needs you</span>
        </div>
        <p class="croc-as-pulse__line" id="asPulseLine">${escapeHtml(pulseLine)}</p>
        <div class="croc-as-pulse__actions">
          <button class="btn sm btn-tap" type="button" id="asQuietScanBtn">Scan recent alarms</button>
          <button class="btn sm secondary btn-tap" type="button" id="asRefreshBtn">Refresh</button>
        </div>
      </div>
      ${capabilityRegistrySoftHtml({
        settings,
        modelUsed: String((waiting[0] && (waiting[0].model_used || waiting[0].model_tier)) || (items[0] && (items[0].model_used || items[0].model_tier)) || ""),
        compact: false,
      })}
    </header>

    ${responseAiInnovHtml("ai")}

    <div class="croc-as-workbench__grid">
      <section class="croc-as-panel glass-panel glass-panel--panel" aria-label="Interrupt queue">
        <div class="croc-as-panel__head">
          <div>
            <p class="incident-workspace__eyebrow">Live</p>
            <h3 class="croc-as-panel__title">Interrupt queue</h3>
          </div>
          <span class="muted" id="asQueueCount">${waiting.length ? `${waiting.length} waiting` : "Clear"}</span>
        </div>
        <div id="asDetailSlot" class="croc-as-detail-slot${detailHtml ? " has-detail" : ""}">${detailHtml}</div>
        <div class="croc-as-queue" id="asQueue">${queueBody}</div>
      </section>

      <aside class="croc-as-side">
        <section class="croc-as-panel glass-panel glass-panel--panel" aria-label="How Sentinel thinks">
          <div class="croc-as-panel__head">
            <div>
              <p class="incident-workspace__eyebrow">Policy</p>
              <h3 class="croc-as-panel__title">How Sentinel thinks</h3>
            </div>
          </div>
          <div class="inline-form croc-as-policy">
            <label class="field"><span>Thinking style</span>
              <select id="asSetMode" disabled>
                <option value="rules" ${mode === "rules" ? "selected" : ""}>Plan rules (fast)</option>
                <option value="hybrid" ${mode === "hybrid" ? "selected" : ""}>Rules + written reasons</option>
                <option value="orchestrator" ${mode === "orchestrator" ? "selected" : ""}>Guided plan (Continue / Needs You)</option>
              </select>
            </label>
            <label class="field"><span>Auto-continue below priority</span>
              <input id="asSetThr" type="number" min="0" max="100" value="${escapeHtml(String(thr))}" disabled />
            </label>
            <p class="muted" style="font-size:12px;margin:0 0 8px;max-width:52ch">Thinking style and auto-continue are constitution — not a tenant rule desk. Human interrupt is Continue / Needs You. This page cannot turn on Approve-every-step.</p>
            <label class="field"><span>Human interrupt</span>
              <select id="asSetHuman" disabled>
                <option value="0" selected>Constitution — Continue / Needs You</option>
              </select>
            </label>
          </div>
          <p class="muted" style="font-size:12px;margin:8px 0 0;max-width:52ch">The Watch runs the incident. Hold / Takeover is human interrupt. Thinking style, auto-continue floor, and interrupt-every-plan are platform constitution, like a model safety baseline.</p>
          <div class="row" style="gap:8px;flex-wrap:wrap;margin-top:12px">
            <button class="btn btn-tap" type="button" id="asSetSaveBtn" disabled>Watch brain is constitution</button>
            <button class="btn sm secondary btn-tap" type="button" id="asSetRollbackBtn">Reset stored overlay</button>
          </div>
          <p id="asSetMsg" class="muted" aria-live="polite"></p>
        </section>

        <section class="croc-as-panel glass-panel glass-panel--panel" aria-label="Force a scan">
          <div class="croc-as-panel__head">
            <div>
              <p class="incident-workspace__eyebrow">On demand</p>
              <h3 class="croc-as-panel__title">Force a plan</h3>
            </div>
          </div>
          <p class="muted" style="margin:0 0 10px;font-size:13px;line-height:1.45">Usually the Watch scans alone. Use this only to force a plan on a specific alarm — then Continue or Hold on Home.</p>
          <div class="inline-form">
            <label class="field wide"><span>Recent alarm</span>
              <select id="asIncidentPick">${recentOptsHtml}</select>
            </label>
          </div>
          <div class="row" style="gap:8px;flex-wrap:wrap;margin-top:10px">
            <button class="btn sm btn-tap" type="button" id="asForceScanBtn">Scan this alarm</button>
          </div>
          <p id="asScanMsg" class="muted" aria-live="polite"></p>
        </section>

        <section class="croc-as-panel glass-panel glass-panel--panel" aria-label="Maintenance tips" id="asTipsPanel">
          <div class="croc-as-panel__head">
            <div>
              <p class="incident-workspace__eyebrow">Before</p>
              <h3 class="croc-as-panel__title">Maintenance tips</h3>
            </div>
          </div>
          <p class="muted" style="margin:0 0 10px;font-size:13px;line-height:1.45">Silent board health hints before the next alarm — never blocks notify.</p>
          <div id="asTipsBody">${tipsHtml || `<p class="muted" style="margin:0;font-size:13px">Tips appear when maintenance checks are open for this site. Readiness and drills stay usable now.</p>`}</div>
          <div class="row" style="gap:8px;flex-wrap:wrap;margin-top:12px">
            <button type="button" class="btn sm secondary btn-tap" data-cc-go="readiness">Before tonight</button>
            <a class="btn sm ghost btn-tap" href="#/group/_all">All boards</a>
          </div>
        </section>
      </aside>
    </div>
  </div>`;
}
